import React, { useState, useMemo } from "react";
import { formatINR } from "../../../shared/services/api";
import { TrendingUp } from "lucide-react";

/**
 * v2.70 Phase 4 — ROICalculator
 * Gross yield = annual_rent / purchase_price * 100
 * Net yield   = (annual_rent - annual_expenses) / purchase_price * 100
 * Payback     = purchase_price / annual_rent (years)
 */
export default function ROICalculator({ defaultPrice = 0, defaultRent = 0 }) {
  const [price, setPrice] = useState(Math.max(0, defaultPrice));
  const [rent, setRent] = useState(Math.max(0, defaultRent));
  const [expensesPctOfRent, setExpensesPct] = useState(15);  // maintenance, brokerage, vacancy

  const { gross, net, payback, annualRent } = useMemo(() => {
    const P = Number(price) || 0;
    const R = Number(rent) || 0;
    const annual = R * 12;
    const expRatio = (Number(expensesPctOfRent) || 0) / 100;
    const netAnnual = annual * (1 - expRatio);
    return {
      annualRent: annual,
      gross: P > 0 ? (annual / P) * 100 : 0,
      net: P > 0 ? (netAnnual / P) * 100 : 0,
      payback: annual > 0 ? P / annual : 0,
    };
  }, [price, rent, expensesPctOfRent]);

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="roi-calculator">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-md bg-[#15803D]/10 text-[#15803D] flex items-center justify-center">
          <TrendingUp size={15} />
        </div>
        <div>
          <h3 className="font-heading font-bold text-base text-[#09090B]">Rental Yield / ROI Calculator</h3>
          <p className="text-[11px] text-[#71717A]">Estimate the annual returns if you rent this out</p>
        </div>
      </div>

      <div className="space-y-3">
        <Field label="Purchase Price (₹)">
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(Number(e.target.value) || 0)}
            className="w-full h-9 px-3 text-[13px] border border-[#E4E4E7] rounded-sm bg-white focus:outline-none focus:border-[#15803D] font-mono-data"
            data-testid="roi-price-input"
          />
        </Field>
        <Field label="Expected Monthly Rent (₹)">
          <input
            type="number"
            value={rent}
            onChange={(e) => setRent(Number(e.target.value) || 0)}
            className="w-full h-9 px-3 text-[13px] border border-[#E4E4E7] rounded-sm bg-white focus:outline-none focus:border-[#15803D] font-mono-data"
            data-testid="roi-rent-input"
          />
        </Field>
        <Field label={`Annual Expenses — Maintenance + Vacancy (${expensesPctOfRent}% of rent)`}>
          <input
            type="range"
            min="0"
            max="50"
            value={expensesPctOfRent}
            onChange={(e) => setExpensesPct(Number(e.target.value))}
            className="w-full accent-[#15803D]"
            data-testid="roi-expenses-slider"
          />
        </Field>
      </div>

      <div className="mt-5 pt-4 border-t border-[#E4E4E7] grid grid-cols-3 gap-2">
        <Stat label="Gross Yield" value={`${gross.toFixed(2)}%`} highlight testid="roi-gross" />
        <Stat label="Net Yield" value={`${net.toFixed(2)}%`} testid="roi-net" />
        <Stat label="Payback" value={`${payback.toFixed(1)} yr`} testid="roi-payback" />
      </div>
      <div className="text-[10.5px] text-[#71717A] mt-2">
        Annual rent: <span className="font-mono-data text-[#09090B]">{formatINR(annualRent)}</span>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider font-semibold text-[#52525B] mb-1 block">{label}</label>
    {children}
  </div>
);

const Stat = ({ label, value, highlight, testid }) => (
  <div className={`rounded-sm p-2.5 ${highlight ? "bg-[#15803D] text-white" : "bg-[#FAFAFA] border border-[#E4E4E7]"}`} data-testid={testid}>
    <div className={`text-[9.5px] uppercase tracking-wider font-semibold ${highlight ? "text-white/70" : "text-[#52525B]"}`}>{label}</div>
    <div className={`font-heading font-bold text-base font-mono-data mt-0.5 ${highlight ? "text-white" : "text-[#09090B]"}`}>{value}</div>
  </div>
);
